import createStyles from '@/components/Create/styles.module.css';
import { userQueryKeys } from '@/config/queryKeys';
import { canManageSuperAdminRole } from '@/config/users';
import { useRoles } from '@/hooks';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { apiClient } from '@/lib/api-client';
import { ROLE_LABELS } from '@/lib/roles';
import { useOrganizations } from '@/pages/Organizations/hooks/useOrganizations';
import { useAppStore } from '@/stores/app';
import type { ButtonProps } from '@nautilusbio/nb-design-system/Button';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Select } from '@nautilusbio/nb-design-system/Select';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import React, { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type AddUserModalProps = {
  orgId: string;
  triggerProps: Omit<ButtonProps, 'data-testid'>;
  onSuccess?: () => void;
};

// Wire values sent to the API. Index-aligned with ROLE_OPTION_LABELS below — the Select
// takes display strings plus a selected index, and the raw lower_snake values are not
// presentable ('org_admin' would render verbatim).
const ROLE_OPTIONS = ['org_admin', 'data_owner'] as const;
const ROLE_OPTION_LABELS = ROLE_OPTIONS.map(role => ROLE_LABELS[role]);

const AddUserModal: FC<AddUserModalProps> = ({ orgId, triggerProps, onSuccess }) => {
  const { t: tUsers } = useTranslation('users');
  const { t: tGeneral } = useTranslation('general');
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const ORG_ID = useAppStore(state => state.selectedOrganization?.id ?? state.selectedOrgStub?.id);
  const { user: currentUser } = useAuthStatus();
  const { isSuperAdmin } = useRoles();
  const isGlobalSuperAdmin = canManageSuperAdminRole(currentUser) && !!ORG_ID && isSuperAdmin(ORG_ID ?? '');

  const { data: orgs = [] } = useOrganizations();
  const orgOptions = useMemo(() => orgs.map(o => o.name), [orgs]);

  const form = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      roleIndex: null as number | null,
      orgIndex: null as number | null,
    },
    onSubmit: async ({ value }) => {
      const selectedRole = typeof value.roleIndex === 'number' ? ROLE_OPTIONS[value.roleIndex] : undefined;
      if (!selectedRole) return;

      const targetOrgId =
        isGlobalSuperAdmin && typeof value.orgIndex === 'number' ? (orgs[value.orgIndex]?.id ?? orgId) : orgId;

      const name = `${value.first_name} ${value.last_name}`.trim();

      const submitPromise = (async () => {
        let userCreated = false;
        try {
          await apiClient.post('/users', {
            email: value.email,
            first_name: value.first_name,
            last_name: value.last_name,
            is_super_admin: false,
          });
          userCreated = true;
          await apiClient.post(`/orgs/${targetOrgId}/users`, {
            email: value.email,
            roles: [selectedRole],
          });
        } catch (err) {
          const detail = err instanceof Error && err.message ? `: ${err.message}` : '';
          const key = userCreated ? 'add_user_org_link_error' : 'add_user_error';
          throw new Error(tUsers(key) + detail, { cause: err });
        }
      })();

      toast.promise(submitPromise, {
        loading: tUsers('add_user_loading'),
        success: tUsers('add_user_success', { name }),
        error: (err: unknown) => (err instanceof Error ? err.message : tUsers('add_user_error')),
      });

      const succeeded = await submitPromise.then(
        () => true,
        () => false,
      );

      if (succeeded) {
        await queryClient.invalidateQueries({ queryKey: userQueryKeys.orgUsers(targetOrgId) });
        setIsOpen(false);
        onSuccess?.();
      }
    },
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) form.reset();
      setIsOpen(open);
    },
    [form],
  );

  const formContent = (
    <div className={createStyles.formContainer}>
      <form
        className={createStyles.form}
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.Field
          name="first_name"
          validators={{
            onChange: ({ value }) => {
              const result = z.string().min(1).safeParse(value);
              if (!result.success) return result.error.issues[0]?.message;
              return undefined;
            },
          }}
        >
          {field => (
            <Input
              className={createStyles.uiInput}
              data-testid="add-user-first-name"
              error={field.state.meta.errors[0]}
              id="add-user-first-name"
              label={{
                children: tGeneral('first_name'),
                id: 'add-user-first-name-label',
                htmlFor: 'add-user-first-name',
              }}
              name={field.name}
              onBlur={field.handleBlur}
              onChange={e => field.handleChange(e.target.value)}
              size="sm"
              value={field.state.value}
            />
          )}
        </form.Field>

        <form.Field
          name="last_name"
          validators={{
            onChange: ({ value }) => {
              const result = z.string().min(1).safeParse(value);
              if (!result.success) return result.error.issues[0]?.message;
              return undefined;
            },
          }}
        >
          {field => (
            <Input
              className={createStyles.uiInput}
              data-testid="add-user-last-name"
              error={field.state.meta.errors[0]}
              id="add-user-last-name"
              label={{ children: tGeneral('last_name'), id: 'add-user-last-name-label', htmlFor: 'add-user-last-name' }}
              name={field.name}
              onBlur={field.handleBlur}
              onChange={e => field.handleChange(e.target.value)}
              size="sm"
              value={field.state.value}
            />
          )}
        </form.Field>

        <form.Field
          name="email"
          validators={{
            onChange: ({ value }) => {
              const result = z.string().email().safeParse(value);
              if (!result.success) return result.error.issues[0]?.message;
              return undefined;
            },
          }}
        >
          {field => (
            <Input
              className={createStyles.uiInput}
              data-testid="add-user-email"
              error={field.state.meta.errors[0]}
              id="add-user-email"
              label={{ children: tGeneral('email'), id: 'add-user-email-label', htmlFor: 'add-user-email' }}
              name={field.name}
              onBlur={field.handleBlur}
              onChange={e => field.handleChange(e.target.value)}
              size="sm"
              type="email"
              value={field.state.value}
            />
          )}
        </form.Field>

        <Stack fullWidth gap={3} orientation="horizontal">
          <div className={createStyles.uiInput}>
            <form.Field
              name="roleIndex"
              validators={{
                onChange: ({ value }) => {
                  if (value === null || value === undefined) return tUsers('validation_role_required');
                  return undefined;
                },
              }}
            >
              {field => (
                <Select
                  className={createStyles.fullWidthSelect}
                  data-testid="add-user-role"
                  id="add-user-role"
                  label={{ children: tGeneral('roles'), id: 'add-user-role-label' }}
                  onChange={field.handleChange}
                  options={ROLE_OPTION_LABELS}
                  placeholder={tUsers('select_role_placeholder')}
                  size="sm"
                  value={field.state.value ?? undefined}
                />
              )}
            </form.Field>
          </div>

          {isGlobalSuperAdmin && (
            <div className={createStyles.uiInput}>
              <form.Field
                name="orgIndex"
                validators={{
                  onChange: ({ value }) => {
                    if (value === null || value === undefined) return tUsers('validation_org_required');
                    return undefined;
                  },
                }}
              >
                {field => (
                  <Select
                    className={createStyles.fullWidthSelect}
                    data-testid="add-user-org"
                    id="add-user-org"
                    label={{ children: tUsers('organization_column_header'), id: 'add-user-org-label' }}
                    onChange={field.handleChange}
                    options={orgOptions}
                    placeholder={tUsers('select_org_placeholder')}
                    size="sm"
                    value={field.state.value ?? undefined}
                  />
                )}
              </form.Field>
            </div>
          )}
        </Stack>

        <Stack fullWidth gap={4} orientation="horizontal">
          <Tooltip
            content={tGeneral('coming_soon')}
            placement="top"
            trigger={
              <Button data-testid="add-user-can-create-studies" isDisabled visual="unstyled">
                {tUsers('can_create_studies')}
              </Button>
            }
          />
          <Tooltip
            content={tGeneral('coming_soon')}
            placement="top"
            trigger={
              <Button data-testid="add-user-can-create-runs" isDisabled visual="unstyled">
                {tUsers('can_create_runs')}
              </Button>
            }
          />
        </Stack>

        <form.Subscribe selector={s => [s.canSubmit, s.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <div className={createStyles.footer}>
              <Button
                data-testid="add-user-submit"
                isDisabled={!canSubmit || isSubmitting}
                isPending={isSubmitting}
                size="xl"
                type="submit"
                visual="primary"
              >
                {tGeneral('submit')}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form>
    </div>
  );

  return (
    <Dialog
      content={formContent}
      data-testid="add-user-dialog"
      onOpenChange={handleOpenChange}
      open={isOpen}
      title={tGeneral('add_user')}
      triggerProps={triggerProps}
    />
  );
};

export default AddUserModal;
